import BaseMap from 'components/BaseMap/BaseMap';
import CustomLeafletControl from 'components/BaseMap/components/CustomLeafletControl';
import { usePerms } from 'contexts/PermsContext';
import {
  selectLinkingGroupId,
  selectSelectedDeviceId,
  selectSelectedDeviceIndex,
} from 'features/integrations/state/selectors/deviceSelectors';
import { selectVisibleDeviceTypes } from 'features/integrations/state/selectors/integrationSelectors';
import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/integrations/utils/deviceComparisionUtils';
import { canViewDeviceType } from 'features/integrations/utils/integrationVisibilityUtils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { Marker, Polygon, Popup } from 'react-leaflet';
import { useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { getJurisdictions } from 'services/geography/geography.service';
import { useFusionGetIntegrationsQuery } from 'services/integrations/integrations.api';
import { resolveGroupDevices } from '../../../Modals/groupSettingsModalUtils';
import ConnectedDevicesLoadingState from '../../components/ConnectedDevicesLoadingState';
import MapLegendIcon from './assets/MapLegendIcon.svg';
import DeviceMarker from './components/DeviceMarker';
import USGSMapLegend from './features/components/USGSMapLegend';
import { getLocationCoordinates, getMapCoords, resolveGroupMapView } from './utils/DeviceMapUtils';

const DeviceMap = ({
  deviceTypeToFilter, // Filter key used by the map filter UI.
  selectedGroup, // Selected group for filter dropdowns (default mode).
  setSelectedGroup, // Setter for selectedGroup (default mode).
  setUseThisDevice = () => {}, // Callback for map marker selection.
  setShowCurrentDevice = () => {}, // Controls whether to show current device details.
  setShowDeviceSettings = () => {}, // Opens device settings from marker popups.
  filtersCompact = false, // Force compact filter UI (widgets/small screens).
  mode = 'default', // "default" uses context devices; "group" uses override list.
  enableFilters = mode !== 'group', // Enable/disable BaseMap filters.
  selectedDeviceOverride = null, // Explicit device to center on (widget-specific).
  devicesOverride = null, // Device list override used in group mode.
  mapBoundsOverride = null, // Explicit bounds for group map framing.
  setShowProvisional = () => {}, // Callback to show provisional data info for USGS.
  usgsMapWidget = false, // Flag to indicate if used within USGS Map Widget
  widget = false, // When true, disables Redux-driven cross-component sync (map centering).
}) => {
  const { data: connectedDevices = [], isLoading: connectedDevicesLoading } =
    useGetConnectedDevicesQuery(); // Fetch devices with node data from the last 24 hours
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const { data: allGroups = [] } = useGetGroupsQuery();
  const selectedDeviceIndex = useSelector(selectSelectedDeviceIndex);
  const selectedDeviceId = useSelector(selectSelectedDeviceId);
  const selectedGroupId = useSelector(selectLinkingGroupId);
  const contextDevices = connectedDevices || [];
  const [legendToggle, setLegendToggle] = useState(false); //Used to control the map Legend display

  const isUSGSConnected = useMemo(() => {
    return integrations.some((integration) => integration.system_type === 'USGS');
  }, [integrations]);

  const devices =
    mode === 'group' && Array.isArray(devicesOverride) ? devicesOverride : contextDevices;
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  const perms = usePerms();
  const canViewUSGS = perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    ['ViewUSGSMonitoringStationConnections'],
  );
  const [mapCenter, setMapCenter] = useState(getMapCoords()); // Default map center
  const [mapZoom, setMapZoom] = useState(16); // Default zoom level
  const [mapBounds, setMapBounds] = useState(null); // Map bounds
  const [userPosition, setUserPosition] = useState(getMapCoords()); // User's current position
  const [jurisdictions, setJurisdictions] = useState([]);

  function parseCoordinate(value) {
    const num = parseFloat(value);
    // Check if the parsed number is usable (not NaN, Infinity, or etc)
    return Number.isFinite(num) ? num : null;
  }

  const viewableDevices = useMemo(() => {
    let filtered =
      mode === 'group'
        ? devices
        : devices.filter((device) => canViewDeviceType(device, perms, visibleDeviceTypes));
    if (deviceTypeToFilter) {
      if (deviceTypeToFilter === 'stage') {
        filtered = filtered.filter((device) => DeviceIsSystemType(device, 'Flood'));
      }
    }
    return filtered;
  }, [deviceTypeToFilter, devices, mode, perms, visibleDeviceTypes]);

  // Determine if there are any devices with valid location data for mapping
  const hasMappableDevices = useMemo(
    () =>
      Array.isArray(viewableDevices) &&
      viewableDevices.some((device) => getLocationCoordinates(device?.location)),
    [viewableDevices],
  );

  useEffect(() => {
    // Get user's current position using Geolocation API (built-in browser API)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const userCoordinates = [
          parseCoordinate(position.coords.latitude),
          parseCoordinate(position.coords.longitude),
        ];
        if (userCoordinates.every((value) => value !== null)) {
          setUserPosition(userCoordinates);
        }
      });
    }
  }, []);

  const prevDeviceIndexRef = useRef(null);
  const prevSelectedIdRef = useRef(null);

  // Center the map whenever selectedDeviceId changes (set by list, graph, or marker clicks).
  // Disabled in widget mode — widgets center via selectedDeviceOverride instead.
  const previousCenteredDeviceIdRef = useRef(null);
  useEffect(() => {
    if (mode === 'group' || widget) return;
    if (selectedDeviceId == null) return;
    if (previousCenteredDeviceIdRef.current === selectedDeviceId) return;
    previousCenteredDeviceIdRef.current = selectedDeviceId;
    const device = devices.find((d) => d.id === selectedDeviceId);
    if (!device) return;
    const coords = getLocationCoordinates(device?.location);
    if (coords) {
      setMapCenter(coords);
      setMapZoom(16);
    }
  }, [selectedDeviceId, devices, mode]);

  useEffect(() => {
    if (mode === 'group') return;
    const selectedDevice = selectedDeviceOverride || devices[selectedDeviceIndex];
    if (!selectedDevice) return;

    if (selectedDeviceOverride) {
      const selectedId = selectedDevice?.id ?? selectedDevice?.node_id ?? null;
      if (prevSelectedIdRef.current === selectedId) return;
      prevSelectedIdRef.current = selectedId;
    } else {
      // Only update if the selected device index actually changed
      if (prevDeviceIndexRef.current === selectedDeviceIndex) return;
      prevDeviceIndexRef.current = selectedDeviceIndex;
    }

    const deviceCoordinates = getLocationCoordinates(selectedDevice?.location);
    if (deviceCoordinates) {
      setMapCenter(deviceCoordinates);
      setMapZoom(16);
    }

    const type = selectedDevice?.device_type;
    if (
      type === 'Manual Road Closure' ||
      type === 'Technology Bridge_Flashing Lights' ||
      type === 'Technology Bridge_Siren/Giant Voice'
    ) {
      if (deviceCoordinates) {
        setMapCenter(deviceCoordinates);
        setMapZoom(16);
      }
      setShowCurrentDevice(false);
    }
  }, [devices, mode, selectedDeviceIndex, selectedDeviceOverride, setShowCurrentDevice]);

  useEffect(() => {
    if (mode === 'group' && mapBoundsOverride) {
      setMapBounds(mapBoundsOverride);
    }
  }, [mapBoundsOverride, mode]);

  // Center map on group devices when a group is selected from the device list.
  const prevLinkingGroupIdRef = useRef(null);
  useEffect(() => {
    if (widget || mode === 'group') return;
    if (selectedGroupId == null) {
      prevLinkingGroupIdRef.current = null; // reset so re-selecting same group fires fresh
      return;
    }
    if (prevLinkingGroupIdRef.current === selectedGroupId) return;
    prevLinkingGroupIdRef.current = selectedGroupId;
    const group = allGroups.find((g) => g.id === selectedGroupId);
    if (!group) return;
    setSelectedGroup?.(group);
    const groupDevices = resolveGroupDevices(connectedDevices, group);
    const mapView = resolveGroupMapView(groupDevices);
    if (!mapView) return;
    if (mapView.type === 'center') {
      setMapCenter(mapView.center);
      setMapZoom(mapView.zoom);
      setMapBounds(null);
    } else {
      setMapBounds(mapView.bounds);
    }
  }, [selectedGroupId, allGroups, connectedDevices, widget, mode]);

  useEffect(() => {
    (async () => {
      try {
        let res = await getJurisdictions();
        res.data.result = res.data.result.filter((f) => f !== null);
        setTimeout(() => {
          setJurisdictions(res.data.result);
        }, 1000);
      } catch (error) {
        console.error('Error fetching jurisdictions:', error);
      }
    })();
  }, []);

  // Only set initial map center once if not already set
  const mapCenterInitialized = useRef(false);
  useEffect(() => {
    if (!mapCenterInitialized.current && !mapCenter && userPosition) {
      setMapCenter(userPosition);
      mapCenterInitialized.current = true;
    }
  }, [userPosition]);

  return (
    <>
      <div className="overflow-hidden rounded-3 h-100 w-100 position-relative">
        <BaseMap
          mapCenter={mapCenter}
          zoom={mapZoom}
          bounds={mapBounds}
          enableFilters={enableFilters && mode !== 'group'}
          deviceTypeToFilter={deviceTypeToFilter}
          setMapBounds={setMapBounds}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          setUseThisDevice={setUseThisDevice}
          setShowCurrentDevice={setShowCurrentDevice}
          // Widget can request compact filter UI via BaseMap.
          filtersCompact={filtersCompact}
        >
          {jurisdictions &&
            jurisdictions.map((jurisdiction, index) => (
              <Polygon
                key={jurisdiction.id || index}
                positions={JSON.parse(jurisdiction.original_coordinates)[0].map(
                  ([longitude, latitude]) => [latitude, longitude],
                )}
                color="purple"
                fillOpacity={0}
              />
            ))}
          {userPosition && (
            <Marker position={userPosition}>
              <Popup>Your location</Popup>
            </Marker>
          )}
          {viewableDevices.map((device, index) => {
            const coords = getLocationCoordinates(device?.location);
            if (!coords) return null;
            const compositeKey = `${device?.system_type ?? 'unknown'}-${device?.id ?? index}`;
            return (
              <DeviceMarker
                key={compositeKey}
                device={device}
                coords={coords}
                setUseThisDevice={setUseThisDevice}
                setMapCenter={setMapCenter}
                setShowCurrentDevice={setShowCurrentDevice}
                setShowDeviceSettings={setShowDeviceSettings}
              />
            );
          })}
          {isUSGSConnected && usgsMapWidget && canViewUSGS && (
            <CustomLeafletControl position="bottomright">
              <div
                className="d-inline-flex flex-row align-items-end justify-content-end gap-0"
                style={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  pointerEvents: 'auto',
                  margin: 0,
                  padding: 0,
                  width: 'max-content',
                  position: 'relative',
                  zIndex: 1100,
                  right: 0,
                  bottom: 0,
                  top: 0,
                  height: '100%',
                }}
              >
                <div className="d-flex flex-column gap-1 px-2">
                  <Button
                    variant="primary"
                    className="text-white p-0"
                    style={{ width: '35px', height: '35px' }}
                    onClick={() => setShowProvisional?.(true)}
                  >
                    <IoMdInformationCircleOutline size={25} />
                  </Button>
                  <Button
                    variant="primary"
                    className="text-white p-0"
                    style={{ width: '35px', height: '35px' }}
                    onClick={() => setLegendToggle((prev) => !prev)}
                  >
                    <img src={MapLegendIcon} alt="Map Legend" height={25} width={25} />
                  </Button>
                </div>
                {legendToggle && (
                  <USGSMapLegend
                    setLegendToggle={setLegendToggle}
                    style={{
                      maxHeight: '100%',
                      maxWidth: '100%',
                      overflow: 'auto',
                      width: 'min(360px, 100%)',
                      borderBottomRightRadius: 0,
                      borderTopRightRadius: 0,
                    }}
                  />
                )}
              </div>
            </CustomLeafletControl>
          )}
        </BaseMap>
        {connectedDevicesLoading && connectedDevices.length === 0 && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 z-1">
            <ConnectedDevicesLoadingState message="Loading map..." />
          </div>
        )}
        {!connectedDevicesLoading && !hasMappableDevices && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-white-50 bg-dark bg-opacity-25 z-1">
            No map data available
          </div>
        )}
      </div>
    </>
  );
};

export default DeviceMap;
